import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { RoomProps } from "../../types";
import { webRoutes } from "../../../../router/webRoutes";
import ChatBubble from "../ChatBubble/ChatBubble";
import YetiCharacter from "../YetiCharacter/YetiCharacter";
import Snowfall from "../Snowfall/Snowfall";
import ObjectShadow from "../../../../components/ObjectShadow/ObjectShadow";
import discoBallImg from "../../../../appAssets/homeTemplate/playCornerImg/discoBall.png";
import doodleBoardImg from "../../../../appAssets/homeTemplate/playCornerImg/doodleBoard.png";
import micImgSrc from "../../../../appAssets/homeTemplate/playCornerImg/micImg.png";
import jokesImg from "../../../../appAssets/homeTemplate/playCornerImg/jokes.png";
import clickAnimationJson from "../../../../appAssets/jsons/ClickAnimation.json";
import "./PlayRoom.scss";
import InputContainer from "../../../WebStoryScreen/Components/InputContainer";
import WebLottiePlayer from "../../../WebStoryScreen/Components/WebLottiePlayer";
import HobbySelector from "../HobbySelector/HobbySelector";
import {
  ExtractNameFromUrl,
  getMatchingObjectNames,
} from "../../../../Utilities/utilFunctions";
import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "../../../../../mixpanel";
import { TrackingEvents } from "../../../../Utilities/analytics";

function PlayRoom({
  storyData,
  activeBlock,
  conversationIndex,
  objectIndex,
  onInputSubmit,
  onTemplateSubmit,
  onButtonAction,
  onTapIndicator,
  onShowOnboardingNavBar,
}: RoomProps) {
  const isOnboardingDone = storyData?.isOnboardingCompleted ?? false;
  const navigate = useNavigate();

  const [showInputText, setShowInputText] = useState({
    state: false,
    textValue: "",
  });
  const [enableHobbySelected, setEnableHobbySelected] = useState(false);
  const [showButtonType, setShowButtonType] = useState({
    state: false,
    textValue: "",
  });
  const [displayHomeObject, setDisplayHomeObject] = useState({
    url: "",
    name: "",
    tapindicator: "",
  });
  const lastHomeObjectUrlRef = useRef("");

  const returnObjectRendering = useCallback(
    (arg0: { name: string; url: string; tapIndicator: string }) => {
      onTapIndicator?.(arg0.tapIndicator);
    },
    [onTapIndicator],
  );
  const handleBackpackContentPress = (contentType: "Video" | "Audio") => {
    const apiUrl = `retrieveMagicBackpack/${contentType}`;
    console.log(apiUrl, "apiUrlapiUrlapiUrl");
    trackEvent(TrackingEvents.object_clicked, {
      object_name: "backpack",
      from: "playroom",
    });
    navigate(webRoutes.magicBackpack, {
      state: { contentType, navigatedFrom: "journeyscreen", apiUrl },
    });
  };

  useEffect(() => {
    if (!storyData?.blocks?.[0]?.conversations) return;
    const conversations = storyData.blocks[0].conversations;
    const currentConversation = conversations[conversationIndex]?.object || [];
    const isLastConversation = conversationIndex === conversations.length - 1;
    const isLastObject = objectIndex === currentConversation.length - 1;
    const tapIndicator =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.tapIndicator;

    if (tapIndicator) {
      returnObjectRendering({
        name: tapIndicator,
        url: "",
        tapIndicator: tapIndicator,
      });
    }

    if (objectIndex < currentConversation.length - 1) {
      if (currentConversation[objectIndex]?.templateElements) {
        setEnableHobbySelected(true);
      }
    }

    if (isLastConversation && isLastObject) {
      if (
        storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[
          objectIndex
        ]?.templateElements
      ) {
        setEnableHobbySelected(true);
      }
      storyData?.blocks?.[0]?.userActions?.forEach(
        (action: { component: string; buttonText: string }) => {
          if (action?.component?.toLowerCase() === "block.text-input") {
            setShowInputText({ state: true, textValue: action.buttonText });
          }
          if (
            action?.component?.toLowerCase() === "block.button-input" &&
            !storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[
              objectIndex
            ]?.templateElements
          ) {
            setShowButtonType({ state: true, textValue: action.buttonText });
          }
        },
      );
    }
  }, [conversationIndex, objectIndex, storyData, returnObjectRendering]);

  const currentObject =
    activeBlock?.conversations?.[conversationIndex]?.object?.[objectIndex] ??
    null;

  useEffect(() => {
    const blockObjects =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.blockObjects;
    const tapIndicator =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.tapIndicator;
    const normalizedBlockObjects = (blockObjects ?? []).map((obj) => ({
      ...obj,
      linkTo: (obj as { linkTo?: string }).linkTo ?? "",
    }));
    const homeObject = ExtractNameFromUrl(normalizedBlockObjects);
    const homeObjectArray = Array.isArray(homeObject)
      ? homeObject
      : [homeObject];

    if (homeObjectArray.length > 0) {
      const homeExist = getMatchingObjectNames(
        [homeObjectArray?.[0]?.name],
        normalizedBlockObjects,
      );

      const incomingUrl = homeExist?.[0]?.url ?? "";
      if (homeExist && lastHomeObjectUrlRef.current !== incomingUrl) {
        lastHomeObjectUrlRef.current = incomingUrl;
        if (homeExist[0]?.name === "Nav") {
          onShowOnboardingNavBar?.({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            indicatorType: tapIndicator ?? "",
          });
        } else {
          setDisplayHomeObject({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            tapindicator: tapIndicator ?? "",
          });
          onShowOnboardingNavBar?.({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            indicatorType: tapIndicator ?? "",
          });
        }
      }
    }
  }, [conversationIndex, storyData, onShowOnboardingNavBar]);

  const userActions = activeBlock?.userActions ?? [];
  const conversationsExhausted =
    (activeBlock?.conversations?.length ?? 0) > 0 &&
    conversationIndex >= (activeBlock?.conversations?.length ?? 0);

  const handleObjectClick = (
    objectName: string,
    route: string,
    state?: Record<string, unknown>,
  ) => {
    console.log(`object_clicked: ${objectName} from: playroom`);
    if (isOnboardingDone) {
      trackEvent(TrackingEvents.object_clicked, {
        object_name: objectName,
        from: "playroom",
      });
    } else {
      trackEvent(TrackingEvents.introduction_completed, {
        type: objectName,
      });
    }
    navigate(route, state ? { state } : undefined);
  };

  console.log("Current Object in PlayRoom:", displayHomeObject);

  return (
    <div className="play-room">
      {/* ── Layer 1: outdoor mountain scene ── */}
      <div
        className="play-room__window-bg"
        style={{
          backgroundImage: `url(${require("../../../../appAssets/outdoorRoom.png")})`,
        }}
      />

      {/* ── Layer 2: snowfall ── */}
      <div className="play-room__snow-wrap">
        <Snowfall duration={6000} maxSize={10} />
      </div>

      {/* ── Layer 3: play room interior PNG ── */}
      <img
        src={require("../../../../appAssets/playroom-web.png")}
        alt=""
        className="play-room__room-img"
      />

      {/* Disco ball — top-center */}
      {isOnboardingDone && (
        <button
          type="button"
          className="play-room__disco"
          onClick={() => handleObjectClick("dance", webRoutes.breatheDance)}
          aria-label="Dance"
        >
          <img src={discoBallImg} alt="" className="play-room__disco-img" />
        </button>
      )}

      {/* Jokes — top-left */}
      {isOnboardingDone && (
        <button
          type="button"
          className="play-room__jokes"
          onClick={() => handleObjectClick("jokes", webRoutes.jokes)}
          aria-label="Jokes"
        >
          <img src={jokesImg} alt="" className="play-room__jokes-img" />
        </button>
      )}

      {/* Yeti */}
      <div className="play-room__yeti">
        <YetiCharacter
          characterImage={currentObject?.characterImage}
          className="play-room__yeti-lottie"
        />
      </div>

      {/* Chat bubble */}
      {currentObject?.content?.trim() && (
        <div
          className={
            isOnboardingDone
              ? "play-room__bubble--right"
              : "play-room__bubble--left"
          }
        >
          <ChatBubble
            content={currentObject.content}
            textColor={currentObject.textColor}
            tailSide={!isOnboardingDone ? "right" : "left"}
            audioUri={currentObject.audio}
          />
        </div>
      )}

      {/* Mic — left side */}
      {isOnboardingDone && (
        <button
          type="button"
          className="play-room__mic"
          onClick={() =>
            handleObjectClick("tell_a_story", webRoutes.chatboxStory)
          }
          aria-label="Tell a story"
        >
          <ObjectShadow
            variant="blue"
            size="100%"
            height="18%"
            offset="0%"
            opacity={0.7}
            style={{
              width: "80%",
              height: "100%",
              justifyContent: "center",
              alignItems: "flex-end",
            }}
          >
            <img src={micImgSrc} alt="" className="play-room__mic-img" />
          </ObjectShadow>
        </button>
      )}
      {/* Doodle board — right side */}
      {isOnboardingDone && (
        <button
          type="button"
          className="play-room__doodle"
          onClick={() =>
            handleObjectClick("doodle", webRoutes.doodling, {
              isOnboardingDone,
            })
          }
          aria-label="Doodle"
        >
          <ObjectShadow
            variant="pink"
            size="120%"
            height="18%"
            offset="-5%"
            style={{ width: "100%", height: "100%" }}
          >
            <img
              src={doodleBoardImg}
              alt=""
              className="play-room__doodle-img"
            />
          </ObjectShadow>
        </button>
      )}

      {isOnboardingDone && (
        <button
          type="button"
          className="play-room__headphone"
          onClick={() => handleBackpackContentPress("Audio")}
          aria-label="Headphone"
        >
          <img
            src={require("../../../../appAssets/headphone.png")}
            alt=""
            className="play-room__headphone-img"
          />
        </button>
      )}

      {/* displayHomeObject — Lottie JSON from blockObjects */}
      {displayHomeObject?.name === "Doodle" && (
        <button
          type="button"
          className="play-room__doodle"
          onClick={() => {
            onButtonAction?.();
            handleObjectClick("doodle", webRoutes.doodling, {
              isOnboardingDone,
            });
          }}
        >
          <ObjectShadow
            variant="pink"
            size="120%"
            height="18%"
            offset="-5%"
            style={{ width: "100%", height: "100%" }}
          >
            <WebLottiePlayer
              source={displayHomeObject.url}
              // className="play-room__homebtn"
              className="play-room__doodle-img"
              loop
              autoPlay
            />
            <div className="play-room__homebtn-indicator">
              <Lottie
                animationData={clickAnimationJson}
                loop
                autoplay
                className="play-room__tap-indicator-lottie"
              />
            </div>
          </ObjectShadow>
        </button>
      )}

      {/* User action buttons */}
      {conversationsExhausted && userActions.length > 0 && (
        <div className="play-room__actions">
          {userActions.map((action, i) => (
            <button
              key={i}
              className={`play-room__action-btn${i === 0 ? " play-room__action-btn--outline" : " play-room__action-btn--filled"}`}
            >
              {action.buttonText}
            </button>
          ))}
        </div>
      )}

      {/* Button-input action — bottom-center, onboarding only */}
      {!isOnboardingDone && showButtonType.state && (
        <div className="play-room__bottom-action">
          <button
            type="button"
            className="play-room__bottom-btn"
            onClick={() => {
              setShowButtonType({ state: false, textValue: "" });
              onButtonAction?.();
              if (displayHomeObject?.name === "Doodle") {
                handleObjectClick("doodle", webRoutes.doodling, {
                  isOnboardingDone,
                });
              }
            }}
          >
            {showButtonType.textValue || "Continue"}
          </button>
        </div>
      )}
    </div>
  );
}

export default PlayRoom;
